import { Trans, t } from "@lingui/macro";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { isAddress } from "viem";
import { useAccount } from "wagmi";

import { APP_NAME } from "config/ui";
import { SyntheticsStateContextProvider } from "context/SyntheticsStateContext/SyntheticsStateContextProvider";
import { useChainId } from "lib/chains";

import AddressView from "components/AddressView/AddressView";
import Footer from "components/Footer/Footer";
import PageTitle from "components/PageTitle/PageTitle";

import { PnlOverview } from "./DailyAndCumulativePnL";
import { GeneralPerformanceDetails } from "./GeneralPerformanceDetails";
import { HistoricalLists, HistoricalListsV1 } from "./HistoricalLists";
import { usePageParams } from "./usePageParams";
import { VersionNetworkSwitcherRow } from "./VersionNetworkSwitcherRow";

export function AccountDashboard() {
  const history = useHistory();
  const { chainId: initialChainId } = useChainId();

  const { chainId, version, account } = usePageParams(initialChainId);
  const { address } = useAccount();
  const lastConnectedAccountRef = useRef<string | undefined>(undefined);

  const versionName = version === 2 ? "V2" : "V1";

  const isYou = address?.toLowerCase() === account?.toLowerCase();
  const shouldRedirectAfterDisconnect =
    !address &&
    Boolean(account) &&
    Boolean(lastConnectedAccountRef.current) &&
    lastConnectedAccountRef.current === account?.toLowerCase();

  useEffect(() => {
    if (address) {
      lastConnectedAccountRef.current = address.toLowerCase();
    }
  }, [address]);

  useEffect(() => {
    if (shouldRedirectAfterDisconnect) {
      history.replace("/trade");
    }
  }, [history, shouldRedirectAfterDisconnect]);

  if (!isAddress(account!)) {
    return (
      <div className="default-container page-layout">
        <PageTitle title={t`GMX ${versionName} Account`} chainId={chainId} />
        <div className="text-center text-red-alpha-100">
          <Trans>Invalid address. Please make sure you have entered a valid Ethereum address</Trans>
        </div>
      </div>
    );
  }

  return (
    <div className="default-container page-layout">
      <PageTitle
        chainId={chainId}
        title={isYou ? t`Portfolio` : t`${APP_NAME} Account`}
        hasBackButton
        subtitle={
          <>
            <div className="flex flex-wrap items-center gap-4">
              {isYou ? (
                <Trans>
                  {/* {APP_NAME} {versionName} {networkName} information for account: */}
                  Your account information
                </Trans>
              ) : (
                <Trans>
                  {/* {APP_NAME} {versionName} {networkName} information for account: */}
                  {APP_NAME} information for account:
                </Trans>
              )}
              {!isYou && <AddressView noLink address={account} size={20} breakpoint="XL" />}
            </div>
            <VersionNetworkSwitcherRow account={account} chainId={chainId} version={version} />
          </>
        }
      />

      {version === 2 && (
        <SyntheticsStateContextProvider overrideChainId={chainId} pageType="accounts" skipLocalReferralCode={false}>
          <div className="mt-12 flex flex-col gap-20 sm:mt-0">
            <div className="flex flex-col gap-32 md:flex-row md:gap-24 lg:gap-34">
              <div className="w-full max-w-full *:size-full md:w-[458px]">
                <GeneralPerformanceDetails chainId={chainId} account={account} />
              </div>
              <div className="grow *:size-full">
                <PnlOverview chainId={chainId} account={account} />
              </div>
            </div>
            <HistoricalLists chainId={chainId} account={account} />
          </div>
        </SyntheticsStateContextProvider>
      )}

      {version === 1 && <HistoricalListsV1 account={account} chainId={chainId} />}

      <Footer />
    </div>
  );
}
